import {
  Button,
  createTableColumn,
  TableCellLayout,
  TableColumnDefinition,
  Theme,
  Tooltip,
} from "@fluentui/react-components";
import { Delete20Regular, EditRegular } from "@fluentui/react-icons";
import { CouponDto } from "../../KulcsUzletSvc";

export const columnSizingOptionsCoupon = {
  code: { minWidth: 120, defaultWidth: 150 },
  discountPercent: { minWidth: 120, defaultWidth: 150 },
  basePrice: { minWidth: 150, defaultWidth: 180 },
  createdAt: { minWidth: 180, defaultWidth: 220 },
  usedCount: { minWidth: 100, defaultWidth: 120 },
  type: { minWidth: 120, defaultWidth: 150 },
  module: { minWidth: 120, defaultWidth: 150 },
  active: { minWidth: 100, defaultWidth: 120 },
};

const isCouponActive = (coupon: CouponDto): boolean => {
  const now = new Date();

  const expired = new Date(coupon.expiredDate ?? new Date());
  if (expired < now) {
    return false;
  }

  const validTo = new Date(coupon.validTo ?? new Date());
  if (validTo >= now) {
    return true; // still within validTo date
  }

  // if validDays is set, calculate based on creationDateTime
  if (coupon.validDays != null) {
    const created = new Date(coupon.creationDateTime ?? new Date());
    const validUntil = new Date(created);
    validUntil.setDate(validUntil.getDate() + coupon.validDays);

    return validUntil >= now;
  }

  return false;
};

export const getColumnsCoupon = (
  theme: Theme,
  navigate: any,
  handleRowAction?: (action: string, item: CouponDto) => void,
) =>
  [
    createTableColumn<CouponDto>({
      columnId: "code",
      renderHeaderCell: () => "Kupon kód",
      renderCell: (item) => (
        <TableCellLayout truncate>{item.couponCode}</TableCellLayout>
      ),
      compare: (a, b) => (a.couponCode ?? "").localeCompare(b.couponCode ?? ""),
    }),

    createTableColumn<CouponDto>({
      columnId: "discountPercent",
      renderHeaderCell: () => "Kedvezmény (% / Összeg)",
      renderCell: (item) => {
        const discountValue = (item as any)?.discountValue;
        const hasDiscountValue =
          discountValue !== undefined && discountValue !== null;

        if (hasDiscountValue) {
          return <TableCellLayout>{Number(discountValue)} Ft</TableCellLayout>;
        }

        const discount = item.discount;
        if (discount === undefined || discount === null) {
          return <TableCellLayout>-</TableCellLayout>;
        }

        return <TableCellLayout>{discount}%</TableCellLayout>;
      },
      compare: (a, b) => {
        const aDv = (a as any)?.discountValue;
        const bDv = (b as any)?.discountValue;
        const aHas = aDv !== undefined && aDv !== null;
        const bHas = bDv !== undefined && bDv !== null;

        const aVal = aHas ? Number(aDv) : Number(a.discount ?? 0);
        const bVal = bHas ? Number(bDv) : Number(b.discount ?? 0);
        return aVal - bVal;
      },
    }),

    createTableColumn<CouponDto>({
      columnId: "basePrice",
      renderHeaderCell: () => "Kulcs-Üzlet alapmodul havi ára (3810Ft)",
      renderCell: (item) => (
        <TableCellLayout>
          {
            // item.modul?.monthPrice
          }{" "}
          Ft
        </TableCellLayout>
      ),
      compare: (a, b) => 0,
    }),

    createTableColumn<CouponDto>({
      columnId: "createdAt",
      renderHeaderCell: () => "Létrehozás dátuma",
      renderCell: (item) => (
        <TableCellLayout>
          {new Date(item.creationDateTime ?? new Date()).toLocaleString(
            "hu-HU",
          )}
        </TableCellLayout>
      ),
      compare: (a, b) => {
        const dateA = new Date(a.creationDateTime ?? 0).getTime();
        const dateB = new Date(b.creationDateTime ?? 0).getTime();
        return dateA - dateB;
      },
    }),

    createTableColumn<CouponDto>({
      columnId: "usedCount",
      renderHeaderCell: () => "Felhasznált mennyiség",
      renderCell: (item) => (
        <TableCellLayout>{(item as any).usedCount ?? "-"}</TableCellLayout>
      ),
      compare: (a, b) =>
        ((a as any).usedCount ?? 0) - ((b as any).usedCount ?? 0),
    }),

    createTableColumn<CouponDto>({
      columnId: "type",
      renderHeaderCell: () => "Típus",
      renderCell: (item) => (
        <TableCellLayout>{item.couponType}</TableCellLayout>
      ),
      compare: (a, b) =>
        `${a.couponType ?? ""}`.localeCompare(`${b.couponType ?? ""}`),
    }),

    createTableColumn<CouponDto>({
      columnId: "module",
      renderHeaderCell: () => "Modul",
      renderCell: (item) => (
        <TableCellLayout>
          {
            // item.modul?.name ?? "-"
          }
        </TableCellLayout>
      ),
      compare: (a, b) => 0,
    }),

    createTableColumn<CouponDto>({
      columnId: "active",
      renderHeaderCell: () => "Aktív",
      renderCell: (item) => (
        <TableCellLayout>
          {isCouponActive(item) ? (
            <span style={{ color: "green", fontWeight: 600 }}>Igen ✔</span>
          ) : (
            <span style={{ color: "red", fontWeight: 600 }}>Nem ✘</span>
          )}
        </TableCellLayout>
      ),
      compare: (a, b) => {
        const aActive = isCouponActive(a) ? 1 : 0;
        const bActive = isCouponActive(b) ? 1 : 0;
        return aActive - bActive;
      },
    }),
    createTableColumn<CouponDto>({
      columnId: "id",
      renderHeaderCell: () => "Műveletek",
      renderCell: (item) => (
        <TableCellLayout>
          <div style={{ display: "flex", gap: "6px" }}>
            <Tooltip content="Kupon szerkesztése" relationship="label">
              <Button
                style={{ backgroundColor: "#5bc0de", color: "white" }}
                icon={<EditRegular />}
                onClick={() => navigate(`/admin/coupon/${item.id}/edit`)}
              />
            </Tooltip>
            <Tooltip content="Kupon törlése" relationship="label">
              <Button
                style={{ backgroundColor: "#d9534f", color: "white" }}
                icon={<Delete20Regular />}
                onClick={() => handleRowAction?.("deleteCoupon", item)}
              />
            </Tooltip>
          </div>
        </TableCellLayout>
      ),
      compare: (a, b) => 0,
    }),
  ] as TableColumnDefinition<CouponDto>[];
